import React, { useState } from 'react';
import { Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
interface TransactionsSectionProps {
  accountType: 'main' | 'futures' | 'inverse-futures' | 'earn';
}
// Mock transaction data
const mockTransactions = {
  main: [{
    time: '12/3/25 1:04 PM',
    asset: 'TRON',
    ticker: 'TRX',
    type: 'Deposit',
    status: 'Failed',
    amount: '0.000003 TRX',
    fee: '0.000000 TRX',
    refId: 'FTPDTm4'
  }, {
    time: '12/3/25 1:04 PM',
    asset: 'TRON',
    ticker: 'TRX',
    type: 'Deposit',
    status: 'Failed',
    amount: '0.000001 TRX',
    fee: '0.000000 TRX',
    refId: 'FTSwpyv'
  }, {
    time: '12/3/25 1:03 PM',
    asset: 'TRON',
    ticker: 'TRX',
    type: 'Deposit',
    status: 'Failed',
    amount: '0.000003 TRX',
    fee: '0.000000 TRX',
    refId: 'FTGwNcn'
  }, {
    time: '12/3/25 1:00 PM',
    asset: 'Tether USD',
    ticker: 'USDT',
    type: 'Convert',
    status: 'Success',
    amount: '16.86370000 USDT',
    fee: '0.0000 USDT',
    refId: 'FTKmGjD'
  }],
  futures: [],
  'inverse-futures': [],
  earn: []
};
export function TransactionsSection({
  accountType
}: TransactionsSectionProps) {
  const [activeTab, setActiveTab] = useState<'transactions' | 'funding' | 'ledger'>('transactions');
  const transactions = mockTransactions[accountType] || [];
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Success':
        return <CheckCircle size={16} className="text-success" data-id="element-1237" />;
      case 'Failed':
        return <XCircle size={16} className="text-danger" data-id="element-1238" />;
      case 'Pending':
        return <Clock size={16} className="text-warning" data-id="element-1239" />;
      default:
        return <AlertCircle size={16} className="text-text-tertiary" data-id="element-1240" />;
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Success':
        return 'text-success';
      case 'Failed':
        return 'text-danger';
      case 'Pending':
        return 'text-warning';
      default:
        return 'text-text-tertiary';
    }
  };
  return <div className="bg-background-secondary border border-border-subtle rounded-[20px] overflow-hidden" data-id="element-1241">
      {/* Header with Tabs */}
      <div className="border-b border-border-subtle" data-id="element-1242">
        <div className="flex items-center justify-between px-6 py-4" data-id="element-1243">
          <div className="flex items-center gap-1" data-id="element-1244">
            <button onClick={() => setActiveTab('transactions')} className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all ${activeTab === 'transactions' ? 'bg-background-elevated text-text-primary' : 'text-text-secondary hover:text-text-primary hover:bg-background-elevated/50'}`} data-id="element-1245">
              Transactions
            </button>
            {accountType === 'main' && <>
                <button onClick={() => setActiveTab('funding')} className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all ${activeTab === 'funding' ? 'bg-background-elevated text-text-primary' : 'text-text-secondary hover:text-text-primary hover:bg-background-elevated/50'}`} data-id="element-1246">
                  Funding
                </button>
                <button onClick={() => setActiveTab('ledger')} className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all ${activeTab === 'ledger' ? 'bg-background-elevated text-text-primary' : 'text-text-secondary hover:text-text-primary hover:bg-background-elevated/50'}`} data-id="element-1247">
                  Ledger
                </button>
              </>}
          </div>
        </div>
      </div>

      {/* Transactions Content */}
      {transactions.length === 0 ? <div className="flex flex-col items-center justify-center py-16" data-id="element-1248">
          <div className="w-16 h-16 rounded-full bg-background-elevated flex items-center justify-center mb-4" data-id="element-1249">
            <Clock size={32} className="text-text-tertiary" data-id="element-1250" />
          </div>
          <p className="text-text-secondary text-sm" data-id="element-1251">No recent activity.</p>
        </div> : <div className="overflow-x-auto" data-id="element-1252">
          <table className="w-full" data-id="element-1253">
            <thead className="bg-background-elevated/50" data-id="element-1254">
              <tr className="border-b border-border-subtle" data-id="element-1255">
                <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-left" data-id="element-1256">
                  Time
                </th>
                <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-left" data-id="element-1257">
                  Asset
                </th>
                <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-left" data-id="element-1258">
                  Type
                </th>
                <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-left" data-id="element-1259">
                  Status
                </th>
                <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-1260">
                  Amount
                </th>
                <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-1261">
                  Fee
                </th>
                <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-1262">
                  Ref ID
                </th>
              </tr>
            </thead>
            <tbody data-id="element-1263">
              {transactions.map((tx, idx) => <tr key={idx} className="border-b border-border-subtle hover:bg-background-elevated/30 transition-colors" data-id="element-1264">
                  <td className="px-6 py-4" data-id="element-1265">
                    <div className="text-sm text-text-primary font-numbers" data-id="element-1266">
                      {tx.time}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-1267">
                    <div className="text-sm text-text-primary" data-id="element-1268">{tx.asset}</div>
                    <div className="text-xs text-text-secondary" data-id="element-1269">
                      {tx.ticker}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-1270">
                    <div className="text-sm text-text-primary" data-id="element-1271">{tx.type}</div>
                  </td>
                  <td className="px-6 py-4" data-id="element-1272">
                    <div className="flex items-center gap-2" data-id="element-1273">
                      {getStatusIcon(tx.status)}
                      <span className={`text-sm font-medium ${getStatusColor(tx.status)}`} data-id="element-1274">
                        {tx.status}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-1275">
                    <div className="text-sm font-numbers text-text-primary" data-id="element-1276">
                      {tx.amount}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-1277">
                    <div className="text-sm font-numbers text-text-primary" data-id="element-1278">
                      {tx.fee}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-1279">
                    <div className="text-sm font-mono text-text-secondary" data-id="element-1280">
                      {tx.refId}
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>}
    </div>;
}